// Formatting utilities
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from "@erp/shared-types";

const BASIC_NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CURRENCY_TO_FORMATTER: { [k: string]: Intl.NumberFormat } = {
  USD: new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
};

export const formatCurrency = (
  value: string | number,
  currency = "USD",
): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "$0.00";

  // Use accounting-style formatting for negative numbers: $(100.00)
  if (numValue < 0) {
    const formatted = BASIC_NUMBER_FORMATTER.format(Math.abs(numValue));
    return `$(${formatted})`;
  }

  let formatter = CURRENCY_TO_FORMATTER[currency];

  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    CURRENCY_TO_FORMATTER[currency] = formatter;
  }

  return formatter.format(numValue);
};

export const formatCurrencyAdvanced = (
  amount: number,
  currency: CurrencyCode = DEFAULT_CURRENCY_CODE,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
) => {
  const value = new Intl.NumberFormat("en-US", {
    ...options,
    style: "currency",
    currency,
  }).format(amount);
  return value;
};

export const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatPercentage = (value: number | string): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return `${numValue}%`;
};
